import React, { useState, useEffect, useMemo } from 'react';
import { useReportData, useUpdateResult, useNewResult } from 'shared/apollo';
import IndicatorsForecastSummaryTable from './IndicatorsForecastSummaryTable';
import { LinearProgress } from '@mui/material';
import styled from 'styled-components';

/*
const STAGES = {
  Learn: 1,
  Act: 2,
  SocialImpact: 3,
  Catalized: 4,
  Reach: 5,
  CapacityDevelopment: 6
}
*/

const Table = ({ reportId, beneficiary }) => {
  /* state */
  const [selectedIndicators, setSelectedIndicators] = useState([]);
  const [updatingIndicatorId, setUpdatingIndicatorId] = useState(undefined);

  /* graphql */
  const withResults = true;
  const { data: report, loading: loadingReport, refetch: reloadReport } = useReportData(reportId, withResults);
  const { run: runNew, isLoading: loadingNew } = useNewResult();
  const { run: runUpdate, isLoading: loadingUpdate } = useUpdateResult();

  /* effects */
  useEffect(() => {
    setSelectedIndicators(report?.indicators ?? []);
  }, [report]);

  /* actions */
  const handleSaveOrUpdate = (data) => {
    if (data && reportId) {
      const form = { ...data, reportId };
      if (form.id) {
        setUpdatingIndicatorId(data.indicatorId);
        runUpdate(form, () => {
          reloadReport().then(() => setUpdatingIndicatorId(undefined));
        });
      } else {
        runNew(form, () => { reloadReport(); });
      }
    }
  };

  const getResult = (indId, results) => {
    const inResults = results?.filter(res => res.indicatorId === indId)?.[0];
    if (inResults) {
      return {
        resultId: inResults?.dbId,
        forecast: inResults?.forecastCurrent ?? 0
      };
    }
    return { resultId: null, forecast: 0 };
  };

  /* constants memo */
  const indicatorsByStage = useMemo(() => {
    const results = report?.results;
    return {
      1: selectedIndicators?.filter(({ stageId }) => stageId === 1).map(item => ({ ...item, ...getResult(item.dbId, results) })),
      2: selectedIndicators?.filter(({ stageId }) => stageId === 2).map(item => ({ ...item, ...getResult(item.dbId, results) })),
      3: selectedIndicators?.filter(({ stageId }) => stageId === 3).map(item => ({ ...item, ...getResult(item.dbId, results) })),
      4: selectedIndicators?.filter(({ stageId }) => stageId === 4).map(item => ({ ...item, ...getResult(item.dbId, results) })),
      5: selectedIndicators?.filter(({ stageId }) => stageId === 5).map(item => ({ ...item, ...getResult(item.dbId, results) })),
      6: selectedIndicators?.filter(({ stageId }) => stageId === 6).map(item => ({ ...item, ...getResult(item.dbId, results) })),
    };
  }, [report, selectedIndicators]);

  const isLoading = (loadingReport || loadingNew || loadingUpdate);

  return (
    <>
      <Progress display={isLoading} />
      <IndicatorsForecastSummaryTable
        stageName="Capacity Development"
        indicators={indicatorsByStage[6]}
        handleSaveOrUpdate={handleSaveOrUpdate}
        beneficiary={beneficiary}
        updatingIndicatorId={updatingIndicatorId}
      />
      <IndicatorsForecastSummaryTable
        stageName="Reach"
        indicators={indicatorsByStage[5]}
        handleSaveOrUpdate={handleSaveOrUpdate}
        beneficiary={beneficiary}
        updatingIndicatorId={updatingIndicatorId}
      />
      <IndicatorsForecastSummaryTable
        stageName="Learn"
        indicators={indicatorsByStage[1]}
        handleSaveOrUpdate={handleSaveOrUpdate}
        beneficiary={beneficiary}
        updatingIndicatorId={updatingIndicatorId}
      />
      <IndicatorsForecastSummaryTable
        stageName="Act"
        indicators={indicatorsByStage[2]}
        handleSaveOrUpdate={handleSaveOrUpdate}
        beneficiary={beneficiary}
        updatingIndicatorId={updatingIndicatorId}
      />
      <IndicatorsForecastSummaryTable
        stageName="Succeed (Social Impact)"
        indicators={indicatorsByStage[3]}
        handleSaveOrUpdate={handleSaveOrUpdate}
        beneficiary={beneficiary}
        updatingIndicatorId={updatingIndicatorId}
      />
    </>
  );
};

export default Table;

export const Progress = styled(LinearProgress)`
  transition: width 0.3s ease-out;
  position: absolute !important;
  background-color: #0072A2 !important;
  width: ${props => props.display ? '100%' : '0%'};
  top: 0px;
  left: 0px;
  .MuiLinearProgress-barColorPrimary {
    background-color: #bccdd1 !important;
  }
`;